import { useTranslations } from 'next-intl';
import { IProduct } from '@monofront/types';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import QuestionMarkOutline from '@monofront/design-system/visuals/Icons/QuestionMarkOutline';
import { useEditorialSidePanelContext } from '@monofront/business/contexts';
import useI18n from '@hooks/i18n/useI18n';

interface IProductScoresProps {
  product: IProduct;
}

const greenscorePaths = {
  'A-PLUS': '/images/greenscores/green-score-A+.svg',
  A: '/images/greenscores/green-score-A.svg',
  B: '/images/greenscores/green-score-B.svg',
  C: '/images/greenscores/green-score-C.svg',
  D: '/images/greenscores/green-score-D.svg',
  E: '/images/greenscores/green-score-E.svg',
  F: '/images/greenscores/green-score-F.svg',
};

const nutriscorePaths = {
  fr: {
    A: '/images/nutriscores/fr/nutriscore-a-new-fr.svg',
    B: '/images/nutriscores/fr/nutriscore-b-new-fr.svg',
    C: '/images/nutriscores/fr/nutriscore-c-new-fr.svg',
    D: '/images/nutriscores/fr/nutriscore-d-new-fr.svg',
    E: '/images/nutriscores/fr/nutriscore-e-new-fr.svg',
  },
  de: {
    A: '/images/nutriscores/de/nutriscore-a-new-de.svg',
    B: '/images/nutriscores/de/nutriscore-b-new-de.svg',
    C: '/images/nutriscores/de/nutriscore-c-new-de.svg',
    D: '/images/nutriscores/de/nutriscore-d-new-de.svg',
    E: '/images/nutriscores/de/nutriscore-e-new-de.svg',
  },
};

const ProductScores: React.FC<IProductScoresProps> = ({ product }) => {
  const tGlobal = useTranslations('Global');
  const tCatalog = useTranslations('Catalog');
  const { locale } = useI18n();
  const { setEditorialPanelSourceUid } = useEditorialSidePanelContext();

  const nutriscoreGrade = product.meta.marketing?.nutriscore_grade;
  const nutriscoreImage = nutriscoreGrade
    ? nutriscorePaths[locale]?.[nutriscoreGrade]
    : null;
  const hasNutriscore = Boolean(nutriscoreGrade && nutriscoreImage);

  const greenscoreGrade = product.meta.marketing?.environmentalscore_grade
  const greenscoreImage = greenscoreGrade
    ? greenscorePaths[greenscoreGrade]
    : null;
  const hasGreenscore = Boolean(greenscoreGrade && greenscoreImage);

  if (!hasGreenscore && !hasNutriscore) return null;

  return (
    <>
      <h3 className="tw-lf-body-xbold-md tw-mb-4 tw-mt-6 [&+span+span]:!tw-ml-3">
        {tCatalog('scores')}
        <button
          className="tw-lf-button-clear tw-ml-2 tw-align-[-2px]"
          type="button"
          aria-label={tGlobal('moreInformation')}
          onClick={() => setEditorialPanelSourceUid('scores')}
        >
          <QuestionMarkOutline aria-hidden UNSAFE_className="tw-size-4" />
        </button>
      </h3>
      {hasNutriscore && (
        <ResponsiveImage
          src={nutriscoreImage}
          alt={tCatalog('nutriscore')}
          width={68}
          height={46}
        />
      )}
      {hasGreenscore && (
        <ResponsiveImage
          src={greenscoreImage}
          alt={tCatalog('greenscore')}
          width={40}
          height={49}
        />
      )}
    </>
  );
};

export default ProductScores;
